import { AgNumberValueCellEditingWithLoaderRenderer } from 'components/AgGrid/AgNumberValueCellEditingWithLoaderRenderer';
import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderPriceData, WorkOrderPriceItem } from 'gql/graphql';
import { WorkOrderStatusEnum, WorkInSumEnum } from 'gql/graphql';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { useUerSetWorkOrderWorkRatio } from '../hooks/useUerSetWorkOrderWorkRatio';

interface IWorkRatioEditCellRendererComponent extends ICellRendererParams<WorkOrderPriceItem | WorkOrderPriceData> {
    onSuccess?: () => void;
    workOrderStatus: WorkOrderStatusEnum;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
}

const requestParams = { isSMRItem: true };

export const WorkRatioEditCellRendererComponent = (props: IWorkRatioEditCellRendererComponent) => {
    const { onSuccess, workOrderStatus, data, useAgCellRowDataRefresh, useAgCellRowDataRefreshParams, api, ...rest } =
        props;

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            api,
            requestParams: { ...useAgCellRowDataRefreshParams, ...requestParams },
        }) || {};

    const onSuccessLocal = useCallback(() => {
        reloadHandler?.([(data as WorkOrderPriceItem)?.itemId]);
        onSuccess?.();
    }, [data, onSuccess, reloadHandler]);

    const isEditable = useMemo(() => {
        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return data?.workInSum === WorkInSumEnum.ParticipateWithRatio;
        }

        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            return !!data?.isActKC2Price && data?.workInSum === WorkInSumEnum.ParticipateWithRatio;
        }

        return false;
    }, [data?.isActKC2Price, data?.workInSum, workOrderStatus]);

    const status = useMemo(() => (isEditable && !data?.workRatio ? 'alert' : undefined), [data?.workRatio, isEditable]);

    const { uerSetWorkOrderWorkRatioMakeRequest, uerSetWorkOrderWorkRatioStatus } = useUerSetWorkOrderWorkRatio({
        workOrderPriceId: (data as WorkOrderPriceData)?.id ?? data?.uerCodeId,
        onSuccess: onSuccessLocal,
    });

    const onChange = useCallback(
        (value) => {
            uerSetWorkOrderWorkRatioMakeRequest(value);
        },
        [uerSetWorkOrderWorkRatioMakeRequest],
    );

    return (
        <AgNumberValueCellEditingWithLoaderRenderer
            decimalScale={4}
            isEditable={isEditable}
            idKey="id"
            columnKey="workRatio"
            debounceTime={500}
            onChange={onChange}
            isShowLoader={uerSetWorkOrderWorkRatioStatus === RequestStatus.FETCHING}
            data={data}
            status={status}
            isNullPossible
            size="xs"
            api={api}
            {...rest}
        />
    );
};

WorkRatioEditCellRendererComponent.displayName = 'WorkRatioEditCellRendererComponent';
